import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'

import type { ReactPayPalScriptOptions } from '@paypal/react-paypal-js/dist/types/types/scriptProviderTypes'
import {
  DISPATCH_ACTION,
  PayPalButtons,
  PayPalMessages,
  usePayPalScriptReducer,
  type PayPalMessagesComponentProps,
  type PayPalButtonsComponentProps,
} from '@paypal/react-paypal-js'
import classNames from 'classnames'

/*eslint-enable */
import { PaymentStore } from 'shop/stores/payment.store'
import { CreateOrderResponse } from 'shop/api/orders.api'
import { Nullable } from 'types/helpers'
import { LoadingMask } from '@elo-kit/components/loading-mask'
import { isProduction } from 'utils/env.utils'
import { ProvidersDataStore } from 'shop/stores/providersData.store'
import * as Sentry from '@sentry/react'

import './paypal-controls.scss'

export type PaypalMessageCurrencies = 'USD' | 'GBP' | 'EUR'

interface paypalMessageProps {
  currency: PaypalMessageCurrencies
  amount: number
  payerCountry?: string
  ignorePayerCounty?: boolean
  isPreview?: boolean
  payPalProps?: PayPalMessagesComponentProps
  withVault?: boolean
  providersDataStore?: ProvidersDataStore
}

interface paypalButtonsProps {
  paymentStore: PaymentStore
  paypalScriptOptions: Partial<ReactPayPalScriptOptions>
  submit: () => Promise<CreateOrderResponse>
  children: React.ReactElement
  style?: {
    color?: 'gold' | 'blue' | 'silver' | 'white' | 'black'
    disableMaxWidth?: boolean
    height?: number
    label?: 'paypal' | 'checkout' | 'buynow' | 'pay' | 'installment' | 'subscribe' | 'donate'
    layout?: 'vertical' | 'horizontal'
    shape?: 'rect' | 'pill'
    tagline?: boolean
    isExpress?: boolean
  }
  buyButtonDisabled?: boolean
  isPreview?: {
    isCabinetPreview?: boolean
    isPreview?: boolean
  }
  isExpress?: boolean
  showPayLater: boolean
  //vault related props
  renewVaultToken?: boolean
  vaultButton: boolean
  shopName?: string
  payPalProvider?: string
  providersDataStore?: ProvidersDataStore
}

export const PayPalBuyButtons = ({
  paypalScriptOptions,
  submit,
  paymentStore,
  children,
  style,
  buyButtonDisabled,
  isPreview,
  isExpress = false,
  showPayLater,
  renewVaultToken,
  payPalProvider,
  shopName,
  providersDataStore,
  vaultButton,
}: paypalButtonsProps) => {
  const [{ options, isPending, isRejected }, dispatch] = usePayPalScriptReducer()
  const [orderData, setOrderData] = useState<Nullable<CreateOrderResponse>>(null)

  const dispatchOptions = () => {
    if (isPending && options.currency === paypalScriptOptions.currency) return

    dispatch({
      type: DISPATCH_ACTION.RESET_OPTIONS,
      value: {
        ...options,
        ...paypalScriptOptions,
        dataUserIdToken: vaultButton && providersDataStore ? providersDataStore.payPalUserIdToken : '',
      },
    })
  }

  useEffect(() => {
    dispatchOptions()

    if (renewVaultToken && providersDataStore && !providersDataStore.renewInterval) {
      providersDataStore.scheduleRenew(shopName, payPalProvider, {}, (token) => {
        dispatch({
          type: DISPATCH_ACTION.RESET_OPTIONS,
          value: {
            ...options,
            dataUserIdToken: vaultButton ? token : '',
          },
        })
      })
    }

    return () => {
      if (renewVaultToken) {
        providersDataStore?.cancelRenew()
      }
    }
  }, [
    paymentStore?.store?.activePlan?.id,
    showPayLater,
    vaultButton,
    paypalScriptOptions.currency,
    ...(!isProduction() ? [paypalScriptOptions.buyerCountry] : []),
  ])

  const buttonProps: PayPalButtonsComponentProps = {
    createOrder: () =>
      submit().then((order) => {
        if (order?.success) {
          setOrderData(order)
          if (order.paypalPaylaterAttributes && order.paypalPaylaterAttributes.orderId) {
            return order.paypalPaylaterAttributes.orderId
          }

          if (order.redirectLink) {
            paymentStore.handleRedirect(order.redirectLink)
          }
        }
        return ''
      }),
    onApprove: (data, actions) => {
      actions.redirect(orderData?.paypalPaylaterAttributes?.returnUrl)

      setTimeout(() => {
        Sentry?.captureException({
          type: 'payPalControlError:onApprove:notRedirected',
          data,
          redirectUrl: orderData?.paypalPaylaterAttributes,
          sellerId: paymentStore?.root?.sellerStore?.item?.id,
          orderId: orderData?.paypalPaylaterAttributes?.orderId,
        })

        if (orderData?.paypalPaylaterAttributes?.returnUrl) {
          window.open(orderData?.paypalPaylaterAttributes?.returnUrl, '_self')
        } else {
          paymentStore.hideLoading()
        }
      }, 20000)

      return new Promise((resolve) => resolve())
    },
    onCancel: (data, actions) => {
      //@ts-ignore
      actions.redirect(orderData?.paypalPaylaterAttributes?.cancelUrl)

      setTimeout(() => {
        Sentry?.captureException({
          type: 'payPalControlError:onCancel:notRedirected',
          data,
          redirectUrl: orderData?.paypalPaylaterAttributes,
          sellerId: paymentStore?.root?.sellerStore?.item?.id,
          orderId: orderData?.paypalPaylaterAttributes?.orderId,
        })
      }, 20000)

      paymentStore.hideLoading()
    },
    onClick: (data, { reject }) => {
      if (!paymentStore.isValidForSubmit && !isExpress) {
        paymentStore.validateForm()
        setTimeout(paymentStore.highlightInvalidFields, 500)
        return reject()
      }
    },
    onError: (err) => {
      Sentry?.captureException({
        type: 'payPalControlError::onError',
        err,
        sellerId: paymentStore?.root?.sellerStore?.item?.id,
        orderId: orderData?.paypalPaylaterAttributes?.orderId,
      })
      paymentStore.hideLoading()
    },
    style,
    onShippingChange: (data, actions) => {
      const shippingCountry = data.shipping_address.country_code
      const isAllowed = paymentStore.getAllowedExpressSellingCountries().includes(shippingCountry)
      if (!isAllowed) {
        return actions.reject()
      }
      return actions.resolve()
    },
  }

  if (isRejected) {
    return <>{children}</>
  }

  const buttonsClasses = classNames('paypal-controls__buttons-container', {
    'paypal-controls__buttons-container--preview': isPreview?.isCabinetPreview,
  })

  return isPending ? (
    <LoadingMask />
  ) : (
    <>
      <PayPalButtons
        className={`${buttonsClasses}`}
        fundingSource='paypal'
        disabled={
          buyButtonDisabled ||
          (!isExpress && paymentStore?.buyBtnDisabled) ||
          isPreview?.isPreview ||
          isPreview?.isCabinetPreview
        }
        {...buttonProps}
      />
      {showPayLater && (
        <PayPalButtons
          className={`${buttonsClasses} paypal-controls__buttons-container--pay-later-button`}
          fundingSource='paylater'
          disabled={
            buyButtonDisabled || paymentStore?.buyBtnDisabled || isPreview?.isPreview || isPreview?.isCabinetPreview
          }
          {...buttonProps}
        />
      )}
    </>
  )
}

export const PaypalPayLaterMessages = observer(function PaypalPayLaterMessages({
  currency,
  amount,
  payerCountry,
  ignorePayerCounty,
  isPreview,
  payPalProps = {},
  withVault = false,
  providersDataStore,
}: paypalMessageProps) {
  const [data, dispatch] = usePayPalScriptReducer()
  const [meta, setMeta] = useState<any>({})
  const style = meta?.offerCountry === payerCountry || ignorePayerCounty ? { display: 'initial' } : { display: 'none' }

  if (data.isRejected) {
    return <></>
  }

  useEffect(() => {
    if (data.isPending) return
    dispatch({
      type: DISPATCH_ACTION.RESET_OPTIONS,
      value: {
        ...data.options,
        currency: currency,
        dataUserIdToken: withVault && providersDataStore ? providersDataStore.payPalUserIdToken : '',
      },
    })
  }, [])

  const messageClasses = classNames('paypal-controls__message-container', {
    'paypal-controls__message-container--preview': isPreview,
  })

  return (
    <div style={style}>
      <PayPalMessages
        className={messageClasses}
        currency={currency}
        forceReRender={[amount, currency]}
        onRender={(data) => setMeta(data.meta || {})}
        amount={amount}
        style={{
          text: {
            size: 12,
          },
        }}
        {...payPalProps}
      />
    </div>
  )
})
